import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import { alpha, useTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MuiTelInput } from 'mui-tel-input';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AddressAutocomplete from '@/components/address-autocomplete';
import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';

import useFalabellaConfig from './useFalabellaConfig';

interface FalabellaConfigProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  pluginId: string;
  clientId: string;
  onSaved?: () => void;
}

const FalabellaConfig = (props: FalabellaConfigProps) => {
  const theme = useTheme();
  const { t } = useTranslation('falabella');
  const hooks = useFalabellaConfig({
    pluginId: props.pluginId,
    clientId: props.clientId,
    isOpen: props.isOpen,
  });

  const onSubmit = hooks.handleSubmit(async (data) => {
    const success = await hooks.saveConfig(data);
    if (success) {
      props.onSaved?.();
      props.onClose();
    }
  });

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t('config.title', { name: props.name })}
      maxWidth="md"
      actions={
        <Button variant="contained" onClick={onSubmit} disabled={hooks.saving}>
          {hooks.saving ? t('config.saving') : t('config.save')}
        </Button>
      }
    >
      {hooks.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Grid container size={12} flexDirection="column" spacing={3} sx={{ mt: 1 }}>
          {/* 1. Dirección de recolección */}
          <Grid size={12}>
            <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
              {t('config.collectAddressTitle')}
            </Typography>
            <Box sx={{ px: 2, pt: 1, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                <Typography
                  color={
                    !hooks.useCustomAddress ? theme.palette.primary.main : theme.palette.grey[500]
                  }
                  fontWeight={!hooks.useCustomAddress ? 'bold' : 'normal'}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    textAlign: { xs: 'center', sm: 'right' },
                  }}
                >
                  {t('config.useFalabellaAddress')}
                </Typography>
                <Controller
                  name="useCustomAddress"
                  control={hooks.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      color="primary"
                    />
                  )}
                />
                <Typography
                  color={
                    hooks.useCustomAddress ? theme.palette.primary.main : theme.palette.grey[500]
                  }
                  fontWeight={hooks.useCustomAddress ? 'bold' : 'normal'}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  {t('config.useCustomAddress')}
                </Typography>
              </Box>
              {!hooks.useCustomAddress && (
                <Typography
                  variant="caption"
                  color={theme.palette.grey[400]}
                  mt={1}
                  display="block"
                  textAlign="center"
                >
                  {t('config.defaultAddressNote')}
                </Typography>
              )}
              {hooks.useCustomAddress && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, sm: 8 }}>
                    <Controller
                      name="collectAddress.address"
                      control={hooks.control}
                      render={({ field, fieldState }) => (
                        <AddressAutocomplete
                          size="small"
                          label={t('config.addressPlaceholder')}
                          placeholder={t('config.addressPlaceholder')}
                          value={field.value || ''}
                          country={hooks.userCountry}
                          onChange={field.onChange}
                          onSelectAddress={hooks.handleSelectAddress}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Controller
                      name="collectAddress.zipCode"
                      control={hooks.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t('config.zipCode')}
                          size="small"
                          value={field.value || ''}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Controller
                      name="collectAddress.references"
                      control={hooks.control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t('config.references')}
                          size="small"
                          value={field.value || ''}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>

          {/* 2. Información de origen / contacto de tienda */}
          <Grid size={12}>
            <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
              {t('config.originInfoTitle')}
            </Typography>
            <Box sx={{ px: 2, pt: 1, width: '100%' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                  width: '100%',
                }}
              >
                <Typography
                  color={
                    !hooks.overrideStoreInfo ? theme.palette.primary.main : theme.palette.grey[500]
                  }
                  fontWeight={!hooks.overrideStoreInfo ? 'bold' : 'normal'}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    textAlign: { xs: 'center', sm: 'right' },
                  }}
                >
                  {t('config.useDefaultStoreInfo')}
                </Typography>
                <Controller
                  name="overrideStoreInfo"
                  control={hooks.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      color="primary"
                    />
                  )}
                />
                <Typography
                  color={
                    hooks.overrideStoreInfo ? theme.palette.primary.main : theme.palette.grey[500]
                  }
                  fontWeight={hooks.overrideStoreInfo ? 'bold' : 'normal'}
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    textAlign: { xs: 'center', sm: 'left' },
                  }}
                >
                  {t('config.useCustomStoreInfo')}
                </Typography>
              </Box>
              {!hooks.overrideStoreInfo && (
                <Typography
                  variant="caption"
                  color={theme.palette.grey[400]}
                  mt={1}
                  display="block"
                  textAlign="center"
                >
                  {t('config.defaultStoreInfoNote')}
                </Typography>
              )}
              {hooks.overrideStoreInfo && (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="storeName"
                      control={hooks.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t('config.storeName')}
                          size="small"
                          value={field.value || ''}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="storeEmail"
                      control={hooks.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label={t('config.storeEmail')}
                          size="small"
                          value={field.value || ''}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Controller
                      name="storePhone"
                      control={hooks.control}
                      render={({ field, fieldState }) => (
                        <MuiTelInput
                          {...field}
                          fullWidth
                          size="small"
                          label={t('config.storePhone')}
                          defaultCountry={hooks.userCountry === 'CHL' ? 'CL' : 'MX'}
                          onlyCountries={['CL', 'MX', 'CO', 'PE']}
                          value={field.value || ''}
                          onChange={(value, info) => {
                            field.onChange(value);
                            hooks.handlePhoneChange(value, info);
                          }}
                          error={!!fieldState.error || hooks.phoneError}
                          helperText={
                            fieldState.error?.message ||
                            (hooks.phoneError ? t('config.phoneInvalid') : undefined)
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>

          {/* 3. Configuraciones adicionales */}
          <Grid size={12}>
            <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
              {t('config.additionalConfigTitle')}
            </Typography>
            <Box sx={{ px: 2, pt: 1, width: '100%' }}>
              <FormControlLabel
                sx={{ gap: '1rem' }}
                control={
                  <Controller
                    name="autoCreateOrder"
                    control={hooks.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    )}
                  />
                }
                label={
                  <Typography color={theme.palette.grey[500]}>
                    {t('config.autoCreateOrder')}
                  </Typography>
                }
              />
              <Box sx={{ mt: 1.5 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info.main, 0.04),
                    borderColor: alpha(theme.palette.info.main, 0.25),
                  }}
                >
                  <div className="flex items-start gap-2.5">
                    <InfoOutlinedIcon
                      color="info"
                      sx={{ fontSize: 18, mt: '1px', flexShrink: 0 }}
                    />
                    <div className="flex flex-col gap-0.5">
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.8rem' }}
                      >
                        {t('config.autoCreateOrderNoteTitle')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.45 }}
                      >
                        {t('config.autoCreateOrderNote')}
                      </Typography>
                    </div>
                  </div>
                </Paper>
              </Box>
            </Box>
          </Grid>

          {/* 4. Seguro adicional */}
          <Grid size={12}>
            <Typography variant="body1" color={theme.palette.grey[500]} fontWeight="bold">
              {t('config.additionalInsuranceTitle')}
            </Typography>
            <Box sx={{ px: 2, pt: 1, width: '100%' }}>
              <FormControlLabel
                sx={{ gap: '1rem' }}
                control={
                  <Controller
                    name="additionalInsurance"
                    control={hooks.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                      />
                    )}
                  />
                }
                label={
                  <Typography color={theme.palette.grey[500]}>
                    {t('config.enableAdditionalInsurance')}
                  </Typography>
                }
              />
              {hooks.additionalInsurance && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid size={12}>
                    <Controller
                      name="additionalInsuranceLimit"
                      control={hooks.control}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size="small"
                          label={t('config.insuranceLimit')}
                          value={field.value || ''}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || t('config.insuranceLimitHelper')}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val !== '' && !/^\d*\.?\d{0,2}$/.test(val)) return;
                            field.onChange(val);
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  {hooks.currencySymbol}
                                </InputAdornment>
                              ),
                            },
                            htmlInput: { inputMode: 'decimal' },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};

export default FalabellaConfig;
